import React, { useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Fade,
  FormControlLabel,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  getCalculationRenderErrorMessageKey,
  isCalculationRenderAbortError,
  runCalculationRender,
} from './calculation-render';

function createIdempotencyKey() {
  if (typeof window.crypto?.randomUUID !== 'function') {
    throw new Error('This browser cannot create an idempotency key.');
  }

  return window.crypto.randomUUID();
}

const CalculationRenderModal = ({
  calculationGuid,
  onClose,
  onTerminal,
  open,
}) => {
  const { t } = useTranslation();
  const activeControllerRef = useRef(null);
  const idempotencyKeyRef = useRef('');
  const [errorMessage, setErrorMessage] = useState('');
  const [hasDurableSubmission, setHasDurableSubmission] = useState(false);
  const [isRetry, setIsRetry] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [mode, setMode] = useState('sequence');
  const [status, setStatus] = useState(null);

  const stopPolling = () => {
    activeControllerRef.current?.abort();
    activeControllerRef.current = null;
  };

  useEffect(() => {
    if (!open) {
      stopPolling();
      return;
    }

    stopPolling();
    idempotencyKeyRef.current = '';
    setErrorMessage('');
    setHasDurableSubmission(false);
    setIsRetry(false);
    setIsRunning(false);
    setMode('sequence');
    setStatus(null);
  }, [calculationGuid, open]);

  useEffect(() => () => {
    stopPolling();
  }, []);

  const startRender = async () => {
    if (activeControllerRef.current !== null) {
      return;
    }

    if (!isRetry || !idempotencyKeyRef.current) {
      idempotencyKeyRef.current = createIdempotencyKey();
    }

    const controller = new AbortController();
    activeControllerRef.current = controller;
    setErrorMessage('');
    setHasDurableSubmission(false);
    setIsRunning(true);
    setStatus({ mode, state: 'queued' });

    try {
      const terminalStatus = await runCalculationRender({
        calculationGuid,
        idempotencyKey: idempotencyKeyRef.current,
        mode,
        onStatusChange: setStatus,
        onSubmissionCreated: (submission) => {
          if (controller.signal.aborted) {
            return;
          }

          setHasDurableSubmission(true);
          setStatus(submission);
        },
        signal: controller.signal,
      });

      if (controller.signal.aborted) {
        return;
      }

      setStatus(terminalStatus);
      idempotencyKeyRef.current = '';
      setIsRetry(false);

      if (
        terminalStatus.state === 'succeeded' ||
        terminalStatus.state === 'partial'
      ) {
        await onTerminal?.(terminalStatus);
      }

      if (terminalStatus.state === 'failed') {
        setErrorMessage(t('calculationRender.errors.renderFailed'));
      }
    } catch (error) {
      if (
        controller.signal.aborted ||
        isCalculationRenderAbortError(error)
      ) {
        return;
      }

      const responseStatus = error?.response?.status;
      const canRetrySameRequest = (
        responseStatus === undefined ||
        responseStatus === 401 ||
        responseStatus === 403 ||
        responseStatus === 408 ||
        responseStatus === 429 ||
        responseStatus >= 500
      );

      setIsRetry(canRetrySameRequest);
      if (!canRetrySameRequest) {
        idempotencyKeyRef.current = '';
      }
      setErrorMessage(t(getCalculationRenderErrorMessageKey(error)));
    } finally {
      if (activeControllerRef.current === controller) {
        activeControllerRef.current = null;
        setIsRunning(false);
      }
    }
  };

  const handleModeChange = (event) => {
    idempotencyKeyRef.current = '';
    setErrorMessage('');
    setHasDurableSubmission(false);
    setIsRetry(false);
    setMode(event.target.value);
    setStatus(null);
  };

  const handleClose = () => {
    stopPolling();
    setHasDurableSubmission(false);
    setIsRunning(false);
    onClose();
  };

  return (
    <Modal
      closeAfterTransition
      onClose={handleClose}
      open={open}
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: 5,
            boxSizing: 'border-box',
            maxWidth: '100%',
            p: { xs: 2.5, sm: 4 },
            position: 'relative',
            width: { xs: 'calc(100vw - 32px)', sm: '520px' },
          }}
        >
          <IconButton
            aria-label={t('calculationRender.buttons.close')}
            onClick={handleClose}
            sx={{ color: 'text.secondary', position: 'absolute', right: 10, top: 10 }}
          >
            <CloseIcon />
          </IconButton>

          <Typography component="h2" sx={{ pr: 5 }} variant="h6">
            {t('calculationRender.title')}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {t('calculationRender.description')}
          </Typography>
          {hasDurableSubmission && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('calculationRender.notice')}
            </Alert>
          )}

          <RadioGroup
            onChange={handleModeChange}
            sx={{ mt: 2 }}
            value={mode}
          >
            <FormControlLabel
              control={<Radio />}
              disabled={isRunning}
              label={t('calculationRender.modes.sequence')}
              value="sequence"
            />
            <FormControlLabel
              control={<Radio />}
              disabled={isRunning}
              label={t('calculationRender.modes.sphere')}
              value="sphere"
            />
          </RadioGroup>

          {status?.state && (
            <Typography sx={{ mt: 2 }}>
              {t('calculationRender.statusLabel')}:{' '}
              {t(`calculationRender.states.${status.state}`, {
                defaultValue: status.state,
              })}
            </Typography>
          )}

          {errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
            <Button
              disabled={isRunning || !calculationGuid}
              onClick={startRender}
              variant="contained"
            >
              {isRunning && <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />}
              {t(
                isRunning
                  ? 'calculationRender.buttons.running'
                  : isRetry
                    ? 'calculationRender.buttons.retry'
                    : 'calculationRender.buttons.start',
              )}
            </Button>
            <Button onClick={handleClose} variant="outlined">
              {t('calculationRender.buttons.close')}
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default CalculationRenderModal;
